import React, { useState, useEffect } from 'react'
import { 
  ArrowLeft,
  Save,
  RotateCcw
} from 'lucide-react'
import Breadcrumb from '../../components/Common/Breadcrumb'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import categoryMasterService from '../../services/categoryMasterService'
import { getUsers } from '../../services/projectService'

const CategoryMasterForm = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const isEditMode = location.pathname.includes('/edit')
  const isViewMode = location.pathname.includes('/view')
  const categoryData = location.state?.categoryData

  const [formData, setFormData] = useState({
    categoryCode: '',
    categoryName: '',
    categoryDescription: '',
    parentCategory: '',
    categoryType: 'General',
    budget: {
      annualBudget: 0,
      currency: 'INR',
      budgetPeriod: 'Annually'
    },
    approvalRequirements: {
      requiresApproval: false,
      approvalLimit: 0,
      approvalCurrency: 'INR',
      approver: '',
      approverName: ''
    },
    taxInformation: {
      taxRate: 0,
      taxType: 'GST',
      hsnCode: '',
      sacCode: ''
    },
    status: 'Active',
    notes: ''
  })

  const [categories, setCategories] = useState([])
  const [users, setUsers] = useState([])
  const [isLoadingCategoryCode, setIsLoadingCategoryCode] = useState(true)
  const [isLoading, setIsLoading] = useState(false)

  // Fetch next category code (only for new categories)
  useEffect(() => {
    if (!isEditMode && !isViewMode) {
      const fetchCategoryCode = async () => {
        try {
          setIsLoadingCategoryCode(true)
          const response = await categoryMasterService.getNextCategoryCode()
          if (response.success && response.data) {
            setFormData(prev => ({
              ...prev,
              categoryCode: response.data.categoryCode || response.data.formattedValue || 'CAT0001'
            }))
          }
        } catch (error) {
          console.error('Error fetching category code:', error)
          setFormData(prev => ({ ...prev, categoryCode: 'CAT0001' }))
        } finally {
          setIsLoadingCategoryCode(false)
        }
      }
      fetchCategoryCode()
    }
  }, [isEditMode, isViewMode])

  // Load categories and users
  useEffect(() => {
    loadCategories()
    loadUsers()
  }, [])

  // Load category data if editing or viewing
  useEffect(() => {
    if ((isEditMode || isViewMode) && categoryData?._id) {
      loadCategoryData(categoryData._id)
    }
  }, [isEditMode, isViewMode, categoryData])

  const loadCategories = async () => {
    try {
      const response = await categoryMasterService.getAllCategories({ status: 'Active', limit: 1000 })
      if (response.success) {
        setCategories(response.data || [])
      }
    } catch (error) {
      console.error('Error loading categories:', error)
    }
  }

  const loadUsers = async () => {
    try {
      const response = await getUsers()
      if (response.success) {
        setUsers(response.data || [])
      }
    } catch (error) {
      console.error('Error loading users:', error)
    }
  }

  const loadCategoryData = async (id) => {
    try {
      setIsLoading(true)
      const response = await categoryMasterService.getCategoryById(id)
      if (response.success && response.data) {
        const category = response.data
        setFormData({
          categoryCode: category.categoryCode || '',
          categoryName: category.categoryName || '',
          categoryDescription: category.categoryDescription || '',
          parentCategory: category.parentCategory?._id || category.parentCategory || '',
          categoryType: category.categoryType || 'General',
          budget: category.budget || {
            annualBudget: 0,
            currency: 'INR',
            budgetPeriod: 'Annually'
          },
          approvalRequirements: category.approvalRequirements || {
            requiresApproval: false,
            approvalLimit: 0,
            approvalCurrency: 'INR',
            approver: '',
            approverName: ''
          },
          taxInformation: category.taxInformation || {
            taxRate: 0,
            taxType: 'GST',
            hsnCode: '',
            sacCode: ''
          },
          status: category.status || 'Active',
          notes: category.notes || ''
        })
      }
    } catch (error) {
      console.error('Error loading category data:', error)
      toast.error('Failed to load category data')
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    
    if (name.startsWith('budget.')) {
      const budgetField = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        budget: {
          ...prev.budget,
          [budgetField]: type === 'number' ? parseFloat(value) || 0 : value
        }
      }))
    } else if (name.startsWith('approvalRequirements.')) {
      const approvalField = name.split('.')[1]
      if (approvalField === 'approver') {
        const selectedUser = users.find(u => u._id === value)
        setFormData(prev => ({
          ...prev,
          approvalRequirements: {
            ...prev.approvalRequirements,
            approver: value,
            approverName: selectedUser ? `${selectedUser.firstName} ${selectedUser.lastName}`.trim() : ''
          }
        }))
      } else {
        setFormData(prev => ({
          ...prev,
          approvalRequirements: {
            ...prev.approvalRequirements,
            [approvalField]: type === 'checkbox' ? checked : (type === 'number' ? parseFloat(value) || 0 : value)
          }
        }))
      }
    } else if (name.startsWith('taxInformation.')) {
      const taxField = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        taxInformation: {
          ...prev.taxInformation,
          [taxField]: type === 'number' ? parseFloat(value) || 0 : value
        }
      }))
    } else if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }))
    } else if (type === 'number') {
      setFormData(prev => ({
        ...prev,
        [name]: parseFloat(value) || 0
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }))
    }
  }

  const handleSave = async () => {
    try {
      // Validation
      if (!formData.categoryName) {
        toast.error('Please fill in all required fields (Category Name)')
        return
      }

      setIsLoading(true)
      
      // Convert parentCategory to null if empty string
      const submitData = {
        ...formData,
        parentCategory: formData.parentCategory || null
      }
      
      if (isEditMode && categoryData?._id) {
        const response = await categoryMasterService.updateCategory(categoryData._id, submitData)
        if (response.success) {
          toast.success('Category updated successfully!')
          navigate('/procurement/category-master')
        } else {
          toast.error(response.message || 'Failed to update category')
        }
      } else {
        const response = await categoryMasterService.createCategory(submitData)
        if (response.success) {
          toast.success('Category created successfully!')
          navigate('/procurement/category-master')
        } else {
          toast.error(response.message || 'Failed to create category')
        }
      }
    } catch (error) {
      console.error('Error saving category:', error)
      if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Invalid data provided')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to save category')
        }
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleReset = () => {
    if (isEditMode || isViewMode) {
      loadCategoryData(categoryData._id)
    } else {
      setFormData(prev => ({
        ...prev,
        categoryName: '',
        categoryDescription: '',
        parentCategory: '',
        categoryType: 'General',
        budget: {
          annualBudget: 0,
          currency: 'INR',
          budgetPeriod: 'Annually'
        },
        approvalRequirements: {
          requiresApproval: false,
          approvalLimit: 0,
          approvalCurrency: 'INR',
          approver: '',
          approverName: ''
        },
        taxInformation: {
          taxRate: 0,
          taxType: 'GST',
          hsnCode: '',
          sacCode: ''
        },
        status: 'Active',
        notes: ''
      }))
    }
  }

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Procurement', path: '/procurement' },
    { label: 'Categories', path: '/procurement/category-master' },
    { 
      label: isEditMode ? 'Edit Category' : isViewMode ? 'View Category' : 'New Category', 
      path: location.pathname, 
      isCurrent: true 
    }
  ]

  if (isLoading && (isEditMode || isViewMode)) {
    return (
      <div className="sales-dashboard">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p style={{ marginTop: '16px', color: 'var(--color-text-secondary, #5B6478)' }}>Loading category...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb items={breadcrumbItems} />

      <div className="page-header">
        <div className="page-title">
          <h1>{isEditMode ? 'Edit Category' : isViewMode ? 'View Category' : 'New Category'}</h1>
          <p>{isEditMode ? 'Update category information' : isViewMode ? 'View category information' : 'Create a new category'}</p>
        </div>
        <div className="page-actions">
          <button 
            className="btn btn-secondary" 
            onClick={() => navigate('/procurement/category-master')}
            style={{
              padding: '10px 20px',
              border: '1px solid #e9ecef',
              borderRadius: '6px',
              background: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              color: '#495057'
            }}
          >
            <ArrowLeft size={16} />
            Back
          </button>
        </div>
      </div>

      <div className="form-container">
        <div className="sac-form-header">
          <h2>Category Information</h2>
        </div>
          
        <form className="form-content">
          <div className="section-header"><h3>Basic Information</h3></div>
          {/* Row 1: Category Code, Category Name, Category Type, Status */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="categoryCode" className="form-label required">
                Category Code
              </label>
              <input
                type="text"
                id="categoryCode"
                name="categoryCode"
                value={isLoadingCategoryCode ? 'Generating...' : formData.categoryCode}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="categoryName" className="form-label required">
                Category Name
              </label>
              <input
                type="text"
                id="categoryName"
                name="categoryName"
                value={formData.categoryName}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                required
                placeholder="e.g., Office Supplies"
              />
            </div>

            <div className="form-group">
              <label htmlFor="categoryType" className="form-label">
                Category Type
              </label>
              <select
                id="categoryType"
                name="categoryType"
                value={formData.categoryType}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="Product">Product</option>
                <option value="Service">Service</option>
                <option value="Expense">Expense</option>
                <option value="Asset">Asset</option>
                <option value="General">General</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="status" className="form-label">
                Status
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </div>

          {/* Row 2: Parent Category */}
          <div className="form-group">
            <label htmlFor="parentCategory" className="form-label">
              Parent Category
            </label>
            <select
              id="parentCategory"
              name="parentCategory"
              value={formData.parentCategory}
              onChange={handleInputChange}
              disabled={isViewMode}
              className="form-input"
            >
              <option value="">None (Top Level)</option>
              {categories
                .filter(cat => !isEditMode || cat._id !== categoryData?._id) // Don't allow selecting self as parent
                .map(category => (
                  <option key={category._id} value={category._id}>
                    {category.categoryName} ({category.categoryCode})
                  </option>
                ))}
            </select>
            <small style={{ color: 'var(--color-text-secondary, #5B6478)', fontSize: '12px', display: 'block', marginTop: '4px' }}>
              Select a parent category to create a hierarchical structure
            </small>
          </div>

          {/* Category Description */}
          <div className="form-group">
            <label htmlFor="categoryDescription" className="form-label">
              Description
            </label>
            <textarea
              id="categoryDescription"
              name="categoryDescription"
              value={formData.categoryDescription}
              onChange={handleInputChange}
              disabled={isViewMode}
              className="form-input"
              rows="3"
              placeholder="Category description..."
            />
          </div>

          {/* Budget Information */}
          <div className="section-header"><h3>Budget Information</h3></div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="budget.annualBudget" className="form-label">
                Annual Budget
              </label>
              <input
                type="number"
                id="budget.annualBudget"
                name="budget.annualBudget"
                value={formData.budget.annualBudget}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                min="0"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label htmlFor="budget.currency" className="form-label">
                Currency
              </label>
              <select
                id="budget.currency"
                name="budget.currency"
                value={formData.budget.currency}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="INR">INR</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="budget.budgetPeriod" className="form-label">
                Budget Period
              </label>
              <select
                id="budget.budgetPeriod"
                name="budget.budgetPeriod"
                value={formData.budget.budgetPeriod}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="Monthly">Monthly</option>
                <option value="Quarterly">Quarterly</option>
                <option value="Semi-Annually">Semi-Annually</option>
                <option value="Annually">Annually</option>
              </select>
            </div>
          </div>

          {/* Approval Requirements */}
          <div className="section-header"><h3>Approval Requirements</h3></div>

          <div className="form-group">
            <label htmlFor="approvalRequirements.requiresApproval" className="form-label">
              Requires Approval
            </label>
            <div style={{ display: 'flex', alignItems: 'center', marginTop: '8px' }}>
              <input
                type="checkbox"
                id="approvalRequirements.requiresApproval"
                name="approvalRequirements.requiresApproval"
                checked={formData.approvalRequirements.requiresApproval}
                onChange={handleInputChange}
                disabled={isViewMode}
                style={{ width: '18px', height: '18px', cursor: isViewMode ? 'not-allowed' : 'pointer' }}
              />
              <label htmlFor="approvalRequirements.requiresApproval" style={{ marginLeft: '8px', fontSize: '14px', color: '#495057' }}>
                Require approval for purchases in this category
              </label>
            </div>
          </div>

          {formData.approvalRequirements.requiresApproval && (
            <div className="grid-row-4">
              <div className="form-group">
                <label htmlFor="approvalRequirements.approvalLimit" className="form-label">
                  Approval Limit
                </label>
                <input
                  type="number"
                  id="approvalRequirements.approvalLimit"
                  name="approvalRequirements.approvalLimit"
                  value={formData.approvalRequirements.approvalLimit}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  className="form-input"
                  min="0"
                  step="0.01"
                />
              </div>

              <div className="form-group">
                <label htmlFor="approvalRequirements.approvalCurrency" className="form-label">
                  Currency
                </label>
                <select
                  id="approvalRequirements.approvalCurrency"
                  name="approvalRequirements.approvalCurrency"
                  value={formData.approvalRequirements.approvalCurrency}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  className="form-input"
                >
                  <option value="INR">INR</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="approvalRequirements.approver" className="form-label">
                  Approver
                </label>
                <select
                  id="approvalRequirements.approver"
                  name="approvalRequirements.approver"
                  value={formData.approvalRequirements.approver}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  className="form-input"
                >
                  <option value="">Select Approver</option>
                  {users.map(user => (
                    <option key={user._id} value={user._id}>
                      {user.firstName} {user.lastName} ({user.email})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Tax Information */}
          <div className="section-header"><h3>Tax Information</h3></div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="taxInformation.taxRate" className="form-label">
                Tax Rate (%)
              </label>
              <input
                type="number"
                id="taxInformation.taxRate"
                name="taxInformation.taxRate"
                value={formData.taxInformation.taxRate}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                min="0"
                max="100"
              />
            </div>

            <div className="form-group">
              <label htmlFor="taxInformation.taxType" className="form-label">
                Tax Type
              </label>
              <select
                id="taxInformation.taxType"
                name="taxInformation.taxType"
                value={formData.taxInformation.taxType}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="GST">GST</option>
                <option value="VAT">VAT</option>
                <option value="Sales Tax">Sales Tax</option>
                <option value="None">None</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="taxInformation.hsnCode" className="form-label">
                HSN Code
              </label>
              <input
                type="text"
                id="taxInformation.hsnCode"
                name="taxInformation.hsnCode"
                value={formData.taxInformation.hsnCode}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="HSN code"
              />
            </div>

            <div className="form-group">
              <label htmlFor="taxInformation.sacCode" className="form-label">
                SAC Code
              </label>
              <input
                type="text"
                id="taxInformation.sacCode"
                name="taxInformation.sacCode"
                value={formData.taxInformation.sacCode}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="SAC code"
              />
            </div>
          </div>

          {/* Notes */}
          <div className="form-group">
            <label htmlFor="notes" className="form-label">
              Notes
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              disabled={isViewMode}
              className="form-input"
              rows="3"
              placeholder="Additional notes..."
            />
          </div>

          {/* Action Buttons */}
          {!isViewMode && (
            <div className="form-actions">
              <button
                type="button"
                onClick={handleSave}
                disabled={isLoading}
                className="btn btn-primary"
                style={{
                  padding: '10px 24px',
                  background: '#292752',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: isLoading ? 0.6 : 1
                }}
              >
                <Save size={16} />
                {isLoading ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                onClick={handleReset}
                disabled={isLoading}
                className="btn btn-secondary"
                style={{
                  padding: '10px 24px',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  background: '#fff',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14px',
                  color: '#495057'
                }}
              >
                <RotateCcw size={16} />
                Reset
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  )
}

export default CategoryMasterForm

